import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { Card, Loading, ModuleHero, Notice } from '../../components/ui';
import { FIELD, FIELD_STYLE } from '../../lib/ui';
import type { FaqItem, GlossaryTerm } from '../../types/workspace';

type FaqResponse = { faqs: FaqItem[]; glossary: GlossaryTerm[] };

/** Filing terms customers meet in their document pack; kept visible even if the API list is trimmed. */
const CORE_TERMS: GlossaryTerm[] = [
  { term: 'AOC-4', meaning: 'Indian MCA form used to file a company’s financial statements with the Registrar of Companies.' },
  { term: 'CARO', meaning: 'Companies (Auditor’s Report) Order — extra auditor reporting requirements that accompany many Indian statutory audits.' },
  { term: 'ITR', meaning: 'Income Tax Return — the annual tax filing that declares taxable income.' },
  { term: 'GSTR', meaning: 'Goods and Services Tax Return — periodic Indian indirect-tax filings that evidence reported turnover.' },
];

function withCoreTerms(list: GlossaryTerm[]): GlossaryTerm[] {
  const have = new Set(list.map((g) => g.term.toLowerCase()));
  return [...CORE_TERMS.filter((t) => !have.has(t.term.toLowerCase())), ...list];
}

export default function Help() {
  const { data, loading, error } = useApi<FaqResponse>('/customer/faqs');
  const [query, setQuery] = useState('');

  const q = query.trim().toLowerCase();
  const faqs = useMemo(
    () => (data?.faqs || []).filter((f) => !q || f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q) || f.tags.some((t) => t.includes(q))),
    [data, q],
  );
  const glossary = useMemo(
    () =>
      withCoreTerms(data?.glossary || []).filter(
        (g) => !q || g.term.toLowerCase().includes(q) || g.meaning.toLowerCase().includes(q) || (g.aliases || []).some((a) => a.toLowerCase().includes(q)),
      ),
    [data, q],
  );

  if (loading) return <Loading />;
  if (error || !data) return <Notice tone="rose">{error || 'Could not load help content.'}</Notice>;

  return (
    <div>
      <ModuleHero
        eyebrow="Help"
        title="Help & FAQ"
        description="Plain-language answers about ratings, documents and sharing, plus a glossary of the jargon you will meet in your pack."
        actions={
          <Link to="/messages" className="rounded-xl bg-[#d6006e] px-4 py-2 text-sm font-bold text-white shadow-[0_8px_24px_rgba(214,0,110,0.28)]">
            Message your analyst
          </Link>
        }
      />

      <div className="mt-6 max-w-md">
        <label htmlFor="help-search" className="mb-1 block text-xs font-semibold text-[#9d0050]">
          Search help
        </label>
        <input
          id="help-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="e.g. Wrong entity, AOC-4, Published"
          className={FIELD}
          style={FIELD_STYLE}
        />
      </div>

      <div className="mt-6 grid gap-5 lg:grid-cols-[1.2fr_0.8fr]">
        <Card title="Frequently asked questions" subtitle={`${faqs.length} answer${faqs.length === 1 ? '' : 's'}`}>
          {faqs.length === 0 ? (
            <p className="text-sm text-[#9a6b82]">No questions match “{query}”.</p>
          ) : (
            <div className="divide-y divide-[#fce7f3]">
              {faqs.map((f) => (
                <details key={f.q} className="group py-3">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-bold text-[#6b0030]">
                    {f.q}
                    <span className="text-lg text-[#d6006e] group-open:rotate-45" aria-hidden>+</span>
                  </summary>
                  <p className="mt-2 text-sm leading-relaxed text-[#4a1a30]">{f.a}</p>
                </details>
              ))}
            </div>
          )}
        </Card>

        <Card title="Glossary" subtitle="Terms used in documents and ratings">
          {glossary.length === 0 ? (
            <p className="text-sm text-[#9a6b82]">No terms match “{query}”.</p>
          ) : (
            <dl className="space-y-3">
              {glossary.map((g) => (
                <div key={g.term}>
                  <dt className="text-sm font-bold text-[#6b0030]">
                    {g.term}
                    {g.aliases && g.aliases.length > 0 && <span className="ml-2 text-xs font-normal text-[#9a6b82]">also: {g.aliases.join(', ')}</span>}
                  </dt>
                  <dd className="mt-0.5 text-sm leading-relaxed text-[#4a1a30]">{g.meaning}</dd>
                </div>
              ))}
            </dl>
          )}
        </Card>
      </div>
    </div>
  );
}
